import { Subject } from '@mui/icons-material';
import { Autocomplete, Box, IconButton, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PermissionsSelectDetailDialog from './PermissionsSelectDetailDialog';
import { PermissionGateApiClient, RoleListDTO } from '../Api/ApiServerHooks';
import { useGateApi } from '../Api/useGateApi';

interface PermissionsSelectComponentProps {
    selected: number | undefined;
    tenantId: number | undefined;
    isMandatory?: boolean;
    label?: string;
    disabled?: boolean;
    onSelect?: (value: RoleListDTO | undefined) => void;
}

export default function PermissionsSelectComponent(props: Readonly<PermissionsSelectComponentProps>) {
    const { selected, tenantId, isMandatory, label, disabled, onSelect } = props;
    const { t } = useTranslation();
    const [selectedValue, setSelectedValue] = useState<RoleListDTO>();
    const [options, setOptions] = useState<RoleListDTO[]>([]);
    const [detailOpen, setDetailOpen] = useState<boolean>(false);
    const theme = useTheme();

    const { authorizedApi } = useGateApi(PermissionGateApiClient);

    useEffect(() => {
        getOptionList();
    }, [tenantId]);

    useEffect(() => {
        setSelectedValue(options.find(p => p.id === selected));
    }, [selected, options]);

    const getOptionList = () => {
        if (!tenantId) { setOptions([]); return; }
        authorizedApi?.getRoles(tenantId).then((res) => {
            const sortedList = res.roles 
                ? [...res.roles].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                : [];
            setOptions(sortedList);
        });
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', margin: '12px', marginLeft: '12px', width: '350px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Autocomplete
                    disablePortal
                    size="small"
                    id="combo-box"
                    options={options}
                    onChange={(e, v) => {
                        setSelectedValue(v ?? undefined);
                        if (onSelect) onSelect(v ?? undefined);
                    }}
                    getOptionLabel={(option) => {
                        if (!option) return '';
                        return (typeof option === 'string') ? option : (option as RoleListDTO).name ?? '';
                    }}
                    renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                            <li key={key} {...otherProps}>
                                {option.name ?? ''}
                            </li>
                        );
                    }}
                    isOptionEqualToValue={(option, value) => option.id === (value as RoleListDTO | undefined)?.id}
                    value={selectedValue ?? null}
                    sx={{ minWidth: 150, border: '0px', flex: 1 }}
                    renderInput={(params) => <TextField {...params} label={label ?? t('Shared.Select')} sx={{ border: '0px' }} />}
                    disabled={disabled ?? false}
                />
                <Tooltip title={t('Administration.openRoleDetail')}>
                    <IconButton color="primary" disabled={disabled ?? false} onClick={() => { setDetailOpen(true); }}>
                        <Subject />
                    </IconButton>
                </Tooltip>
            </Box>

            {!selectedValue && isMandatory &&
                <Typography variant="caption" sx={{ marginTop: '4px', color: theme.palette.error.main }}>
                    {t('Shared.MandatorySelect')}
                </Typography>
            }

            <PermissionsSelectDetailDialog
                open={detailOpen}
                tenantId={tenantId ?? 0}
                selectedValue={selectedValue}
                onClose={() => { getOptionList(); setDetailOpen(false); }}
            />
        </Box>
    )
}
